import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Linkedin } from 'lucide-react';
import { managementTeam } from '../data/mockData';
import SEO from '../components/SEO';
import JsonLd, { breadcrumbSchema, teamPersonSchemas } from '../components/JsonLd';
import { useScrollReveal } from '../hooks/useScrollReveal';

const PersonBlock = ({ person, reverse }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      data-testid={`team-person-${person.id}`}
      className={`grid md:grid-cols-5 gap-8 lg:gap-14 items-start mb-24 last:mb-0 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className={`md:col-span-2 ${reverse ? 'md:order-2' : ''}`}>
        <div className="sticky top-28">
          <div className="relative overflow-hidden rounded-3xl bg-gray-100 aspect-[4/5] shadow-lg">
            <img
              src={person.image}
              alt={person.name}
              className="w-full h-full object-cover object-top"
            />
          </div>
          {person.linkedin && (
            <a
              href={person.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00BCE4] transition-colors"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <Linkedin size={16} />
              Connect on LinkedIn
            </a>
          )}
        </div>
      </div>
      <div className="md:col-span-3">
        <p
          className="text-[10px] font-semibold uppercase tracking-[3px] text-[#00BCE4] mb-3"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {person.role}
        </p>
        <h2
          className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {person.name}
        </h2>
        <div
          className="space-y-4 text-base text-gray-600 leading-relaxed"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {person.bio.map((paragraph, i) => (
            <p key={`${person.id}-p-${i}`}>{paragraph}</p>
          ))}
        </div>
      </div>
    </div>
  );
};

const TeamPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="pt-0 lg:pt-28 pb-20 bg-gray-50 min-h-screen">
      <SEO
        title="Meet the Management Team"
        description="Meet the people behind Imprint MIS — decades of print &amp; packaging MIS expertise, all under one roof in Saffron Walden."
        url="https://imprint-mis.co.uk/team"
      />
      <JsonLd data={breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Meet the Team', path: '/team' },
      ])} />
      {teamPersonSchemas(managementTeam).map((schema) => (
        <JsonLd key={schema.name} data={schema} />
      ))}

      {/* Hero */}
      <section className="bg-white border-b border-gray-100 py-16 lg:py-24">
        <div className="section-container">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#00BCE4] transition-colors mb-6"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <p
            className="text-[10px] font-semibold uppercase tracking-[3px] text-[#00BCE4] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            The People Behind Imprint
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Meet the <span className="text-[#00BCE4]">Management Team</span>
          </h1>
          <p
            className="text-lg text-gray-600 leading-relaxed max-w-3xl"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Between us we&apos;ve spent a lifetime in the print &amp; packaging industry —
            over 150 years of combined experience. When you work with Imprint you get
            direct access to the people who actually build, install and support the software.
          </p>
        </div>
      </section>

      {/* Bios */}
      <section className="py-20 lg:py-28">
        <div className="section-container">
          {managementTeam.map((person, index) => (
            <PersonBlock key={person.id} person={person} reverse={index % 2 === 1} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
